import { i18nNsFactory } from '@atlas-frontend/i18n';
import { FC, memo, useMemo } from 'react';
import { formatDate, getDatePeriodString, sortDate } from '../../common/utils';
import { ExecutionCalendarFormDataType } from '../schema';
import translations from '../translations/CalendarView.translations.json';
import { Table } from '../../common/tanstacktable';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Checkbox, Flex } from 'antd';
import { useShowPastYear } from './useShowPastYear';
import { DataObjectGroupType } from '../types';
import { SystemClassifierItem } from '../../systemClassifier/SystemClassifierItem';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'details-calendar-table'
);
addTranslations(translations);

interface DetailsCalendarTableProps {
  groupType: DataObjectGroupType;
  calendar: ExecutionCalendarFormDataType[];
  loadingType?: string;
}

const columnHelper = createColumnHelper<ExecutionCalendarFormDataType>();

export function DetailsCalendarTable({
  groupType,
  calendar,
  loadingType,
}: DetailsCalendarTableProps) {
  const { t } = useTranslation();
  const {
    showPastYear,
    onChange: onChangeShowPastYear,
    hideOldRows,
  } = useShowPastYear();

  const columns = useMemo(
    () => [
      columnHelper.accessor('executionDate', {
        header: t('column.executionDate.label'),
        meta: { width: 150 },
        enableColumnFilter: false,
        cell: ExecutionDateCell,
        sortingFn: (a, b) =>
          sortDate(a.original.executionDate, b.original.executionDate, true),
      }),
      columnHelper.accessor('validPeriod', {
        header: t('column.validPeriod.label'),
        enableColumnFilter: false,
        enableSorting: false,
        cell: (props) => (
          <ValidPeriodCell {...props} loadingType={loadingType} />
        ),
      }),
      ...(groupType === DataObjectGroupType.O
        ? [
            columnHelper.accessor('sourceDataVersionId', {
              header: t('column.sourceDataVersionId.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 220 },
              cell: SourceDataVersionCell,
            }),
          ]
        : []),
    ],
    [groupType, loadingType, t]
  );

  return (
    <Flex vertical gap={12}>
      <Flex gap="small" align="center">
        <Checkbox
          id="show-past-year"
          checked={showPastYear}
          onChange={onChangeShowPastYear}
        >
          {t('checkbox.show-past-year')}
        </Checkbox>
      </Flex>
      <Table
        name="execution.executionCalendar"
        data={calendar}
        columns={columns}
        rowKey="id"
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        rowHtmlAttributes={hideOldRows}
      />
    </Flex>
  );
}

const ExecutionDateCell: FC<CellContext<any, any>> = memo((props) =>
  formatDate(props.renderValue())
);

const ValidPeriodCell: FC<CellContext<any, any> & { loadingType?: string }> =
  memo((props) =>
    props.loadingType === 'ALL'
      ? t('column.validPeriod.ALL')
      : getDatePeriodString(
          props.row.original.validPeriod[0],
          props.row.original.validPeriod[1]
        )
  );

const SourceDataVersionCell: FC<
  CellContext<ExecutionCalendarFormDataType, any>
> = memo((props) => {
  const itemId = props.renderValue();
  if (!itemId) return null;
  return <SystemClassifierItem code="SOURCE_DATA_VERSION" itemId={itemId} />;
});
